import { useState, useEffect } from 'react';

/**
 * Hook to generate random scalene quadrilateral clip-paths for artistic borders.
 * The quadrilateral changes on each page load/refresh.
 *
 * @param variance - How much randomness to apply (0-1). Default is 0.05 (5% variation)
 * @returns CSS clip-path string
 */
export const useRandomQuadrilateral = (variance: number = 0.05): string => {
  const [clipPath, setClipPath] = useState('');

  useEffect(() => {
    // Generate random offset within variance range
    const getRandomOffset = () => {
      return (Math.random() - 0.5) * variance * 100;
    };

    // Generate four corners with slight random variations
    // Format: polygon(x1 y1, x2 y2, x3 y3, x4 y4)
    const topLeft = {
      x: 0 + getRandomOffset(),
      y: 0 + getRandomOffset()
    };

    const topRight = {
      x: 100 + getRandomOffset(),
      y: 0 + getRandomOffset()
    };

    const bottomRight = {
      x: 100 + getRandomOffset(),
      y: 100 + getRandomOffset()
    };

    const bottomLeft = {
      x: 0 + getRandomOffset(),
      y: 100 + getRandomOffset()
    };

    const path = `polygon(
      ${topLeft.x}% ${topLeft.y}%,
      ${topRight.x}% ${topRight.y}%,
      ${bottomRight.x}% ${bottomRight.y}%,
      ${bottomLeft.x}% ${bottomLeft.y}%
    )`;

    setClipPath(path);
  }, []); // Empty dependency array means this only runs once on mount

  return clipPath;
};

/**
 * Hook to generate random rotation angle for slightly angled lines.
 *
 * @param maxDegrees - Maximum rotation in degrees. Default is 1 degree
 * @returns CSS transform rotate string
 */
export const useRandomRotation = (maxDegrees: number = 1): string => {
  const [rotation, setRotation] = useState('');

  useEffect(() => {
    const randomDegrees = (Math.random() - 0.5) * 2 * maxDegrees;
    setRotation(`rotate(${randomDegrees}deg)`);
  }, [maxDegrees]);

  return rotation;
};
